import { COVERAGE_LEVELS } from '../models/coverage';
import {
  AccountSettings,
  AuditEntry,
  Capability,
  CapabilityMode,
  CatalogReviewItem,
  LineOfBusiness,
  NotificationPreference,
  OwnTemplate,
  Page,
  QuoteSettings,
  RenewalNoticeStep,
  SupportAccess,
  TreeMember,
} from '../models/domain.models';
import {
  defaultAccountSettings,
  defaultQuoteSettings,
  MAX_TOLERANCE_PCT,
  MAX_VALIDITY_DAYS,
  MIN_TOLERANCE_PCT,
  MIN_VALIDITY_DAYS,
} from '../models/platform-defaults';
import {
  AccountView,
  AdminProducerRow,
  AdminSummary,
  AdminTreesView,
  AuditRow,
  CascadePreview,
  CatalogReviewRow,
  ConnectorCapabilityStat,
  ConnectorCompanyHealth,
  ConnectorErrorCategory,
  ConnectorHealth,
  ConnectorHealthView,
  ConnectorPeriod,
  SupportAccessRow,
  TreeChoice,
} from '../models/views.models';
import { descendantsOf, effectiveStatus, hashOf, subtreeOids } from './commission-math';
import { failure, Outcome } from './outcome';
import { addDays } from './rng';
import { DEMO_USERS, MEMBERSHIPS } from './seed-named';
import { StoreHost } from './store-host';
import { TreeEngine } from './tree-engine';

const TWO_FACTOR_CODE = '123456';
const ADMIN_NAME = 'Soporte Plataforma';
const MIN_REASON_LENGTH = 8;
const READ_ONLY_MESSAGE = 'Modo soporte: solo lectura';
const PERIOD_DAYS: Record<ConnectorPeriod, number> = { '7d': 7, '30d': 30, '90d': 90 };
const ERROR_CATEGORIES: ConnectorErrorCategory[] = ['credentials', 'non-quotable-risk', 'invalid-data', 'unavailable', 'rate-limit'];
const CAPABILITIES: Capability[] = [
  'quote', 'issue', 'endorse', 'reportClaim', 'queryClaims', 'queryPolicies', 'documents', 'collections', 'settlements',
];
const QUEUED: Capability[] = ['quote', 'issue'];
const DEFAULT_ERROR_MIX: Record<ConnectorErrorCategory, number> = {
  credentials: 10, 'non-quotable-risk': 0, 'invalid-data': 25, unavailable: 50, 'rate-limit': 15,
};
const COMPANY_PROFILE: Record<string, { success?: number; avgMs?: number; mix?: Partial<Record<ConnectorErrorCategory, number>>; queue?: number }> = {
  'cia-federal': { success: 71.4, avgMs: 4800, mix: { unavailable: 85, 'rate-limit': 5, 'invalid-data': 10, credentials: 0 } },
  'cia-austral': { success: 97.1, avgMs: 11200, queue: 7 },
  'cia-nexo': { success: 88.6, mix: { 'non-quotable-risk': 60, 'invalid-data': 25, unavailable: 15, credentials: 0, 'rate-limit': 0 } },
  'cia-pampa': { success: 91.3, mix: { credentials: 55, unavailable: 25, 'invalid-data': 20, 'rate-limit': 0 } },
};
const QUOTE_ERROR_FIX: Partial<Record<ConnectorErrorCategory, number>> = { 'non-quotable-risk': 20 };

export interface CatalogDecision {
  level: string;
  mappings: { name: string; mappedTo: string }[];
}

export interface AuditQuery {
  user?: string;
  action?: string;
  entity?: string;
  range?: string;
  treeOid?: string;
}

export interface AccountSaveBody {
  phone?: string;
  preferences?: NotificationPreference[];
  renewalNotices?: RenewalNoticeStep[];
}

export interface TemplateBody {
  oid?: string;
  name: string;
  subject: string;
  body: string;
}

function timeNow(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** The platform side: tree choice, support mode, back-office, audit and the stored-only settings (D-030-fe). */
export class PlatformEngine {
  constructor(private readonly host: StoreHost, private readonly tree: TreeEngine) {}

  private get db() {
    return this.host.db;
  }

  private get session() {
    return this.host.session;
  }

  static readOnlyMessage(): string {
    return READ_ONLY_MESSAGE;
  }

  private isAdminSession(): boolean {
    const real = DEMO_USERS.find(u => u.username === this.session.username);
    return real?.role === 'admin' && !this.session.support;
  }

  private adminOnly<T>(run: () => Outcome<T>): Outcome<T> {
    return this.isAdminSession() ? run() : failure(403, 'FORBIDDEN', 'Solo el admin de la plataforma accede al back-office');
  }

  private treeName(oid: string): string {
    return this.db.trees.find(t => t.oid === oid)?.name ?? oid;
  }

  private rootOf(treeOid: string): TreeMember | undefined {
    const root = this.db.trees.find(t => t.oid === treeOid)?.rootOid;
    return this.db.members.find(m => m.oid === root);
  }

  // ── Tree choice (P-03) ────────────────────────────────────────

  treeChoices(username: string): TreeChoice[] {
    return (MEMBERSHIPS[username] ?? []).map(({ treeOid, memberOid }) => {
      const member = this.db.members.find(m => m.oid === memberOid)!;
      const parent = member.parentOid ? this.db.members.find(m => m.oid === member.parentOid) : undefined;
      const effective = effectiveStatus(this.db.members, memberOid);
      const by = effective.byOid && effective.byOid !== memberOid ? this.db.members.find(m => m.oid === effective.byOid) : undefined;
      return {
        treeOid, treeName: this.treeName(treeOid), memberOid,
        roleText: parent ? `Productor hijo de ${parent.name}` : 'Raíz del árbol',
        status: effective.status, suspendedByName: by?.name ?? null,
        lastOperated: this.session.lastOperated[`${username}:${treeOid}`] ?? null,
        canEnter: effective.status === 'active',
      };
    });
  }

  /** Refusal message for a login, or null: a person is blocked only when every one of their trees is closed to them. */
  loginBlock(username: string): string | null {
    const choices = this.treeChoices(username);
    if (choices.length === 0 || choices.some(c => c.canEnter)) return null;
    return choices.length > 1
      ? 'Tu usuario está suspendido en todos tus árboles. Consultá con el productor de cada uno para que lo reactive.'
      : 'Tu usuario está suspendido por tu productor. Consultale para que lo reactive.';
  }

  enterTree(username: string, treeOid: string): Outcome<true> {
    const choice = this.treeChoices(username).find(c => c.treeOid === treeOid);
    if (!choice) return failure(404, 'NOT_FOUND', 'No pertenecés a ese árbol');
    if (!choice.canEnter) return failure(403, 'TREE_SUSPENDED', 'Estás suspendido en ese árbol: no se puede entrar');
    this.session.activeTreeOid = treeOid;
    this.session.lastOperated[`${username}:${treeOid}`] = this.host.today();
    return { value: true };
  }

  // ── Support mode (P-93, D-022-fe) ─────────────────────────────

  enterSupport(treeOid: string, reason: string): Outcome<SupportAccess> {
    return this.adminOnly(() => {
      const root = this.rootOf(treeOid);
      if (!root) return failure(404, 'NOT_FOUND', 'Árbol inexistente');
      if (reason.trim().length < MIN_REASON_LENGTH) {
        return failure(400, 'SUPPORT_REASON_REQUIRED', 'Escribí el motivo del acceso (mínimo 8 caracteres): queda registrado y lo ve el productor');
      }
      const access: SupportAccess = {
        oid: `sup-${String(this.host.nextSequence()).padStart(4, '0')}`, adminName: ADMIN_NAME, treeOid, reason: reason.trim(),
        startDate: this.host.today(), startTime: timeNow(), endDate: null, endTime: null,
      };
      this.db.supportAccesses.unshift(access);
      this.session.support = { accessOid: access.oid, treeOid, reason: access.reason };
      this.host.notify(root.oid, 'system', 'El soporte de la plataforma accedió a tu árbol',
        `Motivo declarado: ${access.reason}. Lo ves en la auditoría de tu árbol.`, null, null);
      return { value: access };
    });
  }

  exitSupport(): Outcome<true> {
    const support = this.session.support;
    if (!support) return failure(409, 'NOT_IN_SUPPORT', 'No estás en modo soporte');
    const access = this.db.supportAccesses.find(a => a.oid === support.accessOid);
    if (access) {
      access.endDate = this.host.today();
      access.endTime = timeNow();
    }
    this.session.support = null;
    return { value: true };
  }

  // ── Back-office (P-90 … P-94) ─────────────────────────────────

  private accessRow(a: SupportAccess): SupportAccessRow {
    return { ...a, treeName: this.treeName(a.treeOid), active: a.endDate === null };
  }

  private accessesNewestFirst(): SupportAccess[] {
    return [...this.db.supportAccesses].sort((a, b) => `${b.startDate} ${b.startTime}`.localeCompare(`${a.startDate} ${a.startTime}`));
  }

  private producerRow(m: TreeMember): AdminProducerRow {
    const parent = m.parentOid ? this.db.members.find(x => x.oid === m.parentOid) : undefined;
    const others = this.db.members.filter(x => x.email === m.email && x.oid !== m.oid).map(x => this.treeName(x.treeOid));
    return {
      memberOid: m.oid, treeOid: m.treeOid, treeName: this.treeName(m.treeOid), name: m.name, license: m.license,
      parentName: parent?.name ?? null, status: m.status, effectiveStatus: effectiveStatus(this.db.members, m.oid).status,
      lastNote: m.statusHistory[m.statusHistory.length - 1]?.text ?? '', otherTrees: others,
    };
  }

  adminTrees(): Outcome<AdminTreesView> {
    return this.adminOnly(() => ({
      value: {
        trees: this.db.trees.map(t => {
          const members = this.db.members.filter(m => m.treeOid === t.oid);
          return {
            oid: t.oid, name: t.name, rootOid: t.rootOid, rootName: members.find(m => m.oid === t.rootOid)?.name ?? '—',
            producers: members.filter(m => m.kind === 'producer').length, sellers: members.filter(m => m.kind === 'seller').length,
            plan: t.plan, paymentStatus: t.paymentStatus, status: effectiveStatus(this.db.members, t.rootOid).status,
          };
        }),
        producers: this.db.members.filter(m => m.kind === 'producer').map(m => this.producerRow(m)),
      },
    }));
  }

  adminSummary(): Outcome<AdminSummary> {
    return this.adminOnly(() => {
      const health = this.connectorHealth('30d');
      const trees = this.db.trees.map(t => effectiveStatus(this.db.members, t.rootOid).status);
      return {
        value: {
          connectors: ('value' in health ? health.value.companies : []).map(c => ({
            companyOid: c.companyOid, companyName: c.companyName, health: c.health, quoteSuccessRate: c.quoteSuccessRate,
          })),
          plansToReview: this.db.catalogReviews.filter(i => i.state === 'pending').length,
          activeTrees: trees.filter(s => s === 'active').length,
          suspendedTrees: trees.filter(s => s !== 'active').length,
          suspendedProducers: this.db.members.filter(m => m.kind === 'producer' && m.status === 'suspended').map(m => this.producerRow(m)),
          recentAccesses: this.accessesNewestFirst().slice(0, 4).map(a => this.accessRow(a)),
        },
      };
    });
  }

  private adminMember(oid: string): TreeMember | undefined {
    return this.db.members.find(m => m.oid === oid && m.kind === 'producer');
  }

  adminCascade(oid: string, action: string): Outcome<CascadePreview> {
    return this.adminOnly(() => {
      const member = this.adminMember(oid);
      if (!member) return failure(404, 'NOT_FOUND', 'Productor inexistente');
      const closed = action === 'baja' ? 'baja' : null;
      return {
        value: {
          member: { oid: member.oid, name: member.name },
          affected: descendantsOf(this.db.members, oid).map(m => ({
            oid: m.oid, name: m.name, kind: m.kind,
            alreadySuspended: closed ? effectiveStatus(this.db.members, m.oid).status === 'baja' : effectiveStatus(this.db.members, m.oid).status !== 'active',
          })),
        },
      };
    });
  }

  adminMemberAction(oid: string, action: 'suspend' | 'reactivate' | 'baja'): Outcome<AdminTreesView> {
    return this.adminOnly(() => {
      const member = this.adminMember(oid);
      if (!member) return failure(404, 'NOT_FOUND', 'Productor inexistente');
      const actor = { oid: null, name: ADMIN_NAME };
      const done = action === 'suspend' ? this.tree.suspendMember(member, actor)
        : action === 'baja' ? this.tree.bajaMember(member, actor)
          : this.tree.reactivateMember(member, actor);
      if ('error' in done) return done;
      return this.adminTrees();
    });
  }

  // ── Catalog review (P-91) ─────────────────────────────────────

  private catalogRow(item: CatalogReviewItem): CatalogReviewRow {
    return { ...structuredClone(item), companyName: this.db.companies.find(c => c.oid === item.companyOid)?.name ?? item.companyOid };
  }

  catalogReviews(): Outcome<CatalogReviewRow[]> {
    return this.adminOnly(() => ({
      value: [...this.db.catalogReviews].sort((a, b) => b.discoveredDate.localeCompare(a.discoveredDate)).map(i => this.catalogRow(i)),
    }));
  }

  confirmCatalog(oid: string, body: CatalogDecision): Outcome<CatalogReviewRow> {
    return this.adminOnly(() => {
      const item = this.db.catalogReviews.find(i => i.oid === oid);
      if (!item) return failure(404, 'NOT_FOUND', 'Plan inexistente');
      if (!COVERAGE_LEVELS[item.lineOfBusiness].includes(body.level)) {
        return failure(400, 'CATALOG_LEVEL_INVALID', 'Elegí un nivel de cobertura del ramo');
      }
      if (body.mappings.length !== item.companyCoverages.length || body.mappings.some(m => !m.mappedTo.trim())) {
        return failure(400, 'CATALOG_MAPPING_INCOMPLETE', 'Cada cobertura de la compañía necesita su cobertura canónica');
      }
      item.companyCoverages = item.companyCoverages.map((c, i) => ({ name: c.name, mappedTo: body.mappings[i].mappedTo.trim() }));
      item.confirmedLevel = body.level;
      item.state = 'confirmed';
      item.decidedDate = this.host.today();
      return { value: this.catalogRow(item) };
    });
  }

  discontinueCatalog(oid: string): Outcome<CatalogReviewRow> {
    return this.adminOnly(() => {
      const item = this.db.catalogReviews.find(i => i.oid === oid);
      if (!item) return failure(404, 'NOT_FOUND', 'Plan inexistente');
      item.state = 'discontinued';
      item.decidedDate = this.host.today();
      return { value: this.catalogRow(item) };
    });
  }

  // ── Connector health (P-92): seeded figures, derived from a hash so they never move ──

  private capabilityStat(companyOid: string, capability: Capability, mode: CapabilityMode, period: ConnectorPeriod): ConnectorCapabilityStat {
    const none = Object.fromEntries(ERROR_CATEGORIES.map(c => [c, 0])) as Record<ConnectorErrorCategory, number>;
    if (mode !== 'api') {
      return { capability, mode, calls: 0, successRate: null, avgMs: null, p95Ms: null, errors: none, queue: 0 };
    }
    const profile = COMPANY_PROFILE[companyOid] ?? {};
    const quote = capability === 'quote';
    const h = hashOf(`${companyOid}:${capability}`);
    const days = PERIOD_DAYS[period];
    const calls = Math.round(((180 + (h % 320)) * days) / 30);
    const drift = days === 7 ? 0.5 : days === 90 ? -0.4 : 0;
    const successRate = Math.min(99.9, (quote && profile.success ? profile.success : 97.8 - (h % 4) * 0.7) + drift);
    const avgMs = quote && profile.avgMs ? profile.avgMs : 420 + (h % 900);
    const failed = Math.round((calls * (100 - successRate)) / 100);
    const mix = { ...DEFAULT_ERROR_MIX, ...(quote ? QUOTE_ERROR_FIX : {}), ...(profile.mix && (quote || capability === 'issue') ? profile.mix : {}) };
    const weight = ERROR_CATEGORIES.reduce((sum, c) => sum + mix[c], 0) || 1;
    const errors = Object.fromEntries(ERROR_CATEGORIES.map(c => [c, Math.round((failed * mix[c]) / weight)])) as Record<ConnectorErrorCategory, number>;
    return {
      capability, mode, calls, successRate: Math.round(successRate * 10) / 10, avgMs, p95Ms: Math.round(avgMs * (quote ? 1.7 : 2.3)),
      errors, queue: QUEUED.includes(capability) ? (profile.queue ?? h % 4) : 0,
    };
  }

  connectorHealth(period: ConnectorPeriod): Outcome<ConnectorHealthView> {
    return this.adminOnly(() => {
      const valid: ConnectorPeriod = period in PERIOD_DAYS ? period : '30d';
      return {
        value: {
          period: valid,
          companies: this.db.companies.map((company): ConnectorCompanyHealth => {
            const stats = CAPABILITIES.map(cap => this.capabilityStat(company.oid, cap, company.capabilities[cap], valid));
            const quote = stats.find(s => s.capability === 'quote');
            const health: ConnectorHealth = (quote?.successRate ?? 100) < 85 ? 'unstable' : (quote?.avgMs ?? 0) > 8000 ? 'slow' : 'ok';
            return { companyOid: company.oid, companyName: company.name, health, quoteSuccessRate: quote?.successRate ?? null, stats };
          }),
        },
      };
    });
  }

  // ── Audit (P-88, P-94) ────────────────────────────────────────

  private entries(): AuditEntry[] {
    const fromAccess = this.db.supportAccesses.map((a): AuditEntry => ({
      oid: `aud-${a.oid}`, treeOid: a.treeOid, memberOid: null, userName: a.adminName, action: 'support-access', entityType: 'tree',
      entityLabel: `${this.treeName(a.treeOid)} — ${a.reason}`, date: a.startDate, time: a.startTime,
    }));
    return [...this.db.auditLog, ...fromAccess].sort((a, b) => `${b.date} ${b.time}`.localeCompare(`${a.date} ${a.time}`));
  }

  private filtered(entries: AuditEntry[], query: AuditQuery): AuditEntry[] {
    const since = query.range && query.range in PERIOD_DAYS ? addDays(this.host.today(), -PERIOD_DAYS[query.range as ConnectorPeriod]) : null;
    return entries.filter(e => (!query.user || e.userName === query.user) && (!query.action || e.action === query.action)
      && (!query.entity || e.entityType === query.entity) && (!query.treeOid || e.treeOid === query.treeOid) && (!since || e.date >= since));
  }

  private toRow(e: AuditEntry): AuditRow {
    return {
      oid: e.oid, treeOid: e.treeOid, treeName: this.treeName(e.treeOid), userName: e.userName, action: e.action,
      entityType: e.entityType, entityLabel: e.entityLabel, date: e.date, time: e.time,
    };
  }

  private page(rows: AuditRow[], page: number, size: number): Page<AuditRow> {
    return { data: rows.slice((page - 1) * size, page * size), total: rows.length };
  }

  /** P-88: what happened in the producer's own subtree; the admin's accesses are listed apart. */
  myAudit(query: AuditQuery, page: number, size: number): Page<AuditRow> {
    const user = this.host.user();
    if (!user?.memberOid || user.role !== 'producer') return { data: [], total: 0 };
    const owners = subtreeOids(this.db.members, user.memberOid);
    const mine = this.entries().filter(e => e.action !== 'support-access' && e.memberOid !== null && owners.has(e.memberOid));
    return this.page(this.filtered(mine, query).map(e => this.toRow(e)), page, size);
  }

  myAuditUsers(): string[] {
    const user = this.host.user();
    if (!user?.memberOid) return [];
    const owners = subtreeOids(this.db.members, user.memberOid);
    return this.db.members.filter(m => owners.has(m.oid)).map(m => m.name).sort((a, b) => a.localeCompare(b));
  }

  mySupportAccesses(): SupportAccessRow[] {
    const user = this.host.user();
    if (!user?.treeOid || user.role !== 'producer') return [];
    const root = this.db.trees.find(t => t.oid === user.treeOid)?.rootOid;
    if (user.memberOid !== root) return [];
    return this.accessesNewestFirst().filter(a => a.treeOid === user.treeOid).map(a => this.accessRow(a));
  }

  globalAudit(query: AuditQuery, page: number, size: number): Outcome<Page<AuditRow>> {
    return this.adminOnly(() => ({ value: this.page(this.filtered(this.entries(), query).map(e => this.toRow(e)), page, size) }));
  }

  globalAuditUsers(): string[] {
    return [...new Set(this.entries().map(e => e.userName))].sort((a, b) => a.localeCompare(b));
  }

  // ── Settings: stored and shown only (D-024-fe) ────────────────

  private settingsFor(username: string): AccountSettings {
    const existing = this.db.accountSettings[username];
    if (existing) return existing;
    const memberOid = MEMBERSHIPS[username]?.[0]?.memberOid;
    const created = defaultAccountSettings(this.db.members.find(m => m.oid === memberOid)?.phone ?? '');
    this.db.accountSettings[username] = created;
    return created;
  }

  accountView(): Outcome<AccountView> {
    const username = this.session.username;
    const demo = DEMO_USERS.find(u => u.username === username);
    if (!username || !demo) return failure(401, 'NO_SESSION', 'Sin sesión');
    return {
      value: {
        username, nombre: demo.nombre, apellido: demo.apellido, email: demo.email, role: demo.role,
        settings: structuredClone(this.settingsFor(username)),
        trees: this.treeChoices(username),
        subscriptions: (MEMBERSHIPS[username] ?? []).map(({ treeOid, memberOid }) => {
          const tree = this.db.trees.find(t => t.oid === treeOid)!;
          return {
            treeName: tree.name, plan: tree.plan, paymentStatus: tree.paymentStatus,
            isRoot: this.db.members.find(m => m.oid === memberOid)?.parentOid === null,
          };
        }),
      },
    };
  }

  saveAccount(body: AccountSaveBody): Outcome<AccountView> {
    const username = this.session.username;
    if (!username) return failure(401, 'NO_SESSION', 'Sin sesión');
    const settings = this.settingsFor(username);
    if (body.phone !== undefined) settings.phone = body.phone.trim();
    if (body.preferences) settings.preferences = body.preferences.map(p => ({ ...p, notifyClient: !!p.notifyClient }));
    if (body.renewalNotices) {
      const invalid = body.renewalNotices.some(n => n.daysBefore.length === 0 || n.daysBefore.length > 4
        || n.daysBefore.some(d => !Number.isInteger(d) || d < 1 || d > 180));
      if (invalid) return failure(400, 'NOTICES_INVALID', 'Cada aviso de renovación lleva de 1 a 4 plazos, de 1 a 180 días antes');
      settings.renewalNotices = body.renewalNotices.map(n => ({
        lineOfBusiness: n.lineOfBusiness as LineOfBusiness, daysBefore: [...n.daysBefore].sort((a, b) => b - a),
      }));
    }
    return this.accountView();
  }

  setTwoFactor(enabled: boolean, code: string): Outcome<AccountView> {
    const username = this.session.username;
    if (!username) return failure(401, 'NO_SESSION', 'Sin sesión');
    if (code !== TWO_FACTOR_CODE) return failure(400, 'TWO_FACTOR_CODE_INVALID', 'Código inválido o vencido');
    this.settingsFor(username).twoFactorEnabled = enabled;
    return this.accountView();
  }

  /** Validated and acknowledged only: the demo password is never stored and logins keep accepting any password. */
  changePassword(current: string, next: string): Outcome<true> {
    if (!current) return failure(400, 'PASSWORD_CURRENT_REQUIRED', 'Ingresá tu contraseña actual');
    if (next.length < 8) return failure(400, 'PASSWORD_TOO_SHORT', 'La nueva contraseña necesita al menos 8 caracteres');
    if (next === current) return failure(400, 'PASSWORD_UNCHANGED', 'La nueva contraseña tiene que ser distinta de la actual');
    return { value: true };
  }

  saveTemplate(body: TemplateBody): Outcome<AccountView> {
    const username = this.session.username;
    if (!username) return failure(401, 'NO_SESSION', 'Sin sesión');
    if (!body.name.trim() || !body.subject.trim() || !body.body.trim()) {
      return failure(400, 'TEMPLATE_INVALID', 'La plantilla necesita nombre, asunto y texto');
    }
    const templates = this.settingsFor(username).templates;
    const existing = body.oid ? templates.find(t => t.oid === body.oid) : undefined;
    if (existing) {
      Object.assign(existing, { name: body.name.trim(), subject: body.subject.trim(), body: body.body.trim() });
    } else {
      const created: OwnTemplate = {
        oid: `tpl-${this.host.nextSequence()}`, name: body.name.trim(), subject: body.subject.trim(), body: body.body.trim(),
      };
      templates.push(created);
    }
    return this.accountView();
  }

  deleteTemplate(oid: string): Outcome<AccountView> {
    const username = this.session.username;
    if (!username) return failure(401, 'NO_SESSION', 'Sin sesión');
    const settings = this.settingsFor(username);
    settings.templates = settings.templates.filter(t => t.oid !== oid);
    return this.accountView();
  }

  quoteSettings(): Outcome<QuoteSettings> {
    const key = this.host.user()?.memberOid;
    if (!key) return failure(403, 'FORBIDDEN', 'La configuración de cotización es del productor');
    return { value: structuredClone(this.db.quoteSettings[key] ?? defaultQuoteSettings()) };
  }

  saveQuoteSettings(body: QuoteSettings): Outcome<QuoteSettings> {
    const user = this.host.user();
    if (!user?.memberOid || user.role !== 'producer') return failure(403, 'FORBIDDEN', 'La configuración de cotización es del productor');
    if (!Number.isFinite(body.tolerancePct) || body.tolerancePct < MIN_TOLERANCE_PCT || body.tolerancePct > MAX_TOLERANCE_PCT) {
      return failure(400, 'TOLERANCE_INVALID', `La tolerancia va de ${MIN_TOLERANCE_PCT} a ${MAX_TOLERANCE_PCT} %`);
    }
    if (!Number.isInteger(body.offerValidityDays) || body.offerValidityDays < MIN_VALIDITY_DAYS || body.offerValidityDays > MAX_VALIDITY_DAYS) {
      return failure(400, 'VALIDITY_INVALID', `La vigencia va de ${MIN_VALIDITY_DAYS} a ${MAX_VALIDITY_DAYS} días`);
    }
    this.db.quoteSettings[user.memberOid] = {
      tolerancePct: body.tolerancePct, offerValidityDays: body.offerValidityDays, pdfContact: body.pdfContact.trim(),
      pdfLegalText: body.pdfLegalText.trim(), pdfLogoName: body.pdfLogoName.trim(),
    };
    return this.quoteSettings();
  }
}
